import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';
import { setShowingDialogState } from 'src/redux/slices/showDialog.slice';
import { RootState } from 'src/redux/store';
import { fpost, fput } from '../../../lib/fetch';
import { configWaterLevel, configVelocity, dataVelocity, dataWaterLevel } from '../dataChart/DoubleChart';
import '../../map-dialog/components/WaterLevel.css';
import { Button } from '../../../lib/components/Button.component';
import { ResponseListDocuments } from '../../dashboard/dto/response';
import { DeviceRecord } from '../../dashboard/dto/device/deviceRecord';
import { toasting } from '../../../lib/components/Toast.component';
// @ts-ignore
import { CSVLink } from 'react-csv';
import { AiOutlineReload } from 'react-icons/ai';
import { twMerge } from 'tailwind-merge';
import { option } from "../../dashboard/interfaces/option";

export function DialogWaterLevel() {
  const showDialog = useSelector((state: RootState) => state.showDialogReducer.showDialogWaterLevel);
  const dataDialog = useSelector((state: RootState) => state.showDialogReducer.position);

  const [isReloading, setIsReloading] = useState(false);
  const csvLinkRef = useRef<any>(null);
  const [minVelocitySetting, setMinVelocitySetting] = useState<number | null>(null);
  const [maxVelocitySetting, setMaxVelocitySetting] = useState<number | null>(null);
  const [minWaterRateSetting, setMinWaterRateSetting] = useState<number | null>(null);
  const [maxWaterRateSetting, setMaxWaterRateSetting] = useState<number | null>(null);
  const [minWaterLevelSetting, setMinWaterLevelSetting] = useState<number | null>(null);
  const [maxWaterLevelSetting, setMaxWaterLevelSetting] = useState<number | null>(null);
  const [timeRangeHours, setTimeRangeHours] = useState(1); // 1-3 hours

  const showPlaceholder = !dataDialog || !dataDialog.deviceId;

  const handleMinMax = async (e: any, inputCase: string) => {
    const newValue = e.target.value;
    switch (inputCase) {
      case 'minVelocitySetting':
        setMinVelocitySetting(newValue);
        break;
      case 'maxVelocitySetting':
        setMaxVelocitySetting(newValue);
        break;
      case 'minWaterRateSetting':
        setMinWaterRateSetting(newValue);
        break;
      case 'maxWaterRateSetting':
        setMaxWaterRateSetting(newValue);
        break;
      case 'minWaterLevelSetting':
        setMinWaterLevelSetting(newValue);
        break;
      case 'maxWaterLevelSetting':
        setMaxWaterLevelSetting(newValue);
        break;
    }
  };

  const optionSettings: option = {
    minVelocitySetting: minVelocitySetting,
    maxVelocitySetting: maxVelocitySetting,
    minWaterRateSetting: minWaterRateSetting,
    maxWaterRateSetting: maxWaterRateSetting,
    minWaterLevelSetting: minWaterLevelSetting,
    maxWaterLevelSetting: maxWaterLevelSetting,
  }

  const getTimeWithZero = (time: number) => {
    return time < 10 ? '0' + time : time;
  }

  const setChartLabelString = () => {
    const now = new Date();
    const startTime = new Date(now.getTime() - timeRangeHours * 60 * 60 * 1000);
    
    const formatTime = (date: Date) => {
      return `${getTimeWithZero(date.getHours())}:${getTimeWithZero(date.getMinutes())}`;
    };
    
    const label = `${formatTime(startTime)} - ${formatTime(now)} (${timeRangeHours}h)`;
    configWaterLevel.options.scales.xAxes[0].scaleLabel.labelString = label;
    configVelocity.options.scales.xAxes[0].scaleLabel.labelString = label;
  };

  const getAdjustedValue = (min: number, max: number) => {
    const range = max - min;
    const scalingFactor = 0.75;

    const adjustedMin = min - range * scalingFactor;
    const adjustedMax = max + range * scalingFactor;

    return [
      adjustedMin,
      adjustedMax,
    ];
  }

  const resetGraphData = (quantity: number) => {
    dataWaterLevel.datasets[0].data = Array.from({ length: quantity }, () => null);
    dataVelocity.datasets[0].data = Array.from({ length: quantity }, () => null);
  }

  const getLastHourData = async () => {
    const deviceId = dataDialog?.deviceId;
    if (!deviceId) return;

    const now = new Date();
    const startTime = new Date(now.getTime() - timeRangeHours * 60 * 60 * 1000);
    const totalMinutes = timeRangeHours * 60;

    // Xác định khoảng cách giữa các mốc label theo số giờ
    let labelInterval: number;
    if (timeRangeHours === 1) {
      labelInterval = 3; // 3 phút 1 mốc
    } else if (timeRangeHours === 2) {
      labelInterval = 6; // 6 phút 1 mốc
    } else {
      labelInterval = 9; // 9 phút 1 mốc cho 3 giờ
    }

    // Tạo labels theo phút
    const labels = Array.from({ length: totalMinutes }, (_, index) => {
      const time = new Date(startTime.getTime() + index * 60 * 1000);
      // Hiển thị label theo khoảng cách đã định
      if (index % labelInterval === 0 || index === totalMinutes - 1) {
        return `${getTimeWithZero(time.getHours())}:${getTimeWithZero(time.getMinutes())}`;
      }
      return '';
    });
    
    dataVelocity.labels = labels;
    dataWaterLevel.labels = labels;

    // get records
    const query = {
      query: {
        ExpressionAttributeValues: {
          ':id': { S: deviceId },
          ':timeEnd': { S: now.getTime().toString() },
          ':timeStart': { S: startTime.getTime().toString() },
        },
        KeyConditionExpression: 'DEVICE_ID = :id and CREATE_AT BETWEEN :timeStart and :timeEnd',
        TableName: 'DEVICE_RECORD',
        ScanIndexForward: true,
      },
    };

    const data = await fpost<any>('api/device/record/query', {
      body: query,
    });
    const items = data[1].Items;

    resetGraphData(totalMinutes);

    let maxWaterLevel = 0;
    let maxVelocity = 2;
    let minWaterLevel = 100;
    let minVelocity = 0;

    for (const item of items) {
      const itemTime = new Date(parseInt(item.CREATE_AT.S));
      // Tính index dựa trên thời gian của item so với startTime
      const minutesDiff = Math.floor((itemTime.getTime() - startTime.getTime()) / (60 * 1000));
      
      if (minutesDiff >= 0 && minutesDiff < totalMinutes) {
        if (item.WATER_LEVEL) {
          const waterLevel = parseFloat(item.WATER_LEVEL.S);
          dataWaterLevel.datasets[0].data[minutesDiff] = waterLevel;

          maxWaterLevel = Math.max(maxWaterLevel, waterLevel);
          minWaterLevel = Math.min(waterLevel, minWaterLevel);
        }

        if (item.VELOCITY) {
          const velocity = parseFloat(item.VELOCITY.S);
          dataVelocity.datasets[0].data[minutesDiff] = velocity;

          maxVelocity = Math.max(maxVelocity, velocity);
          minVelocity = Math.min(minVelocity, velocity);
        }
      }
    }

    const [adjustedMinWaterLevel, adjustedMaxWaterLevel] = getAdjustedValue(minWaterLevel, maxWaterLevel);
    const [adjustedMinVelocity, adjustedMaxVelocity] = getAdjustedValue(minVelocity, maxVelocity);
    
    if (optionSettings.maxWaterLevelSetting && optionSettings.minWaterLevelSetting) {
      configWaterLevel.options.scales.yAxes[0].ticks.min = Number(optionSettings.minWaterLevelSetting.toFixed(2));
      configWaterLevel.options.scales.yAxes[0].ticks.max = Number(optionSettings.maxWaterLevelSetting.toFixed(2));
    } else {
      configWaterLevel.options.scales.yAxes[0].ticks.min = Number(adjustedMinWaterLevel.toFixed(2));
      configWaterLevel.options.scales.yAxes[0].ticks.max = Number(adjustedMaxWaterLevel.toFixed(2));
    }

    if (optionSettings.minVelocitySetting && optionSettings.maxVelocitySetting) {
      configVelocity.options.scales.yAxes[0].ticks.min = Number(optionSettings.minVelocitySetting.toFixed(2));
      configVelocity.options.scales.yAxes[0].ticks.max = Number(optionSettings.maxVelocitySetting.toFixed(2));
    } else {
      configVelocity.options.scales.yAxes[0].ticks.min = Number(adjustedMinVelocity.toFixed(2));
      configVelocity.options.scales.yAxes[0].ticks.max = Number(adjustedMaxVelocity.toFixed(2));
    }
  }

  const handleZoomIn = () => {
    if (timeRangeHours > 1) {
      setTimeRangeHours(timeRangeHours - 1);
    }
  };

  const handleZoomOut = () => {
    if (timeRangeHours < 3) {
      setTimeRangeHours(timeRangeHours + 1);
    }
  };

  const handleReload = useCallback(async () => {
    
    setIsReloading(true);
    setChartLabelString();
    await getLastHourData();
    setIsReloading(false);
  }, [dataDialog?.deviceId, minVelocitySetting, maxVelocitySetting, minWaterLevelSetting, maxWaterLevelSetting, timeRangeHours]);

  useEffect(() => {
    if (showPlaceholder) return;
    const interval = setInterval(() => {
      handleReload();
    }, 60000);

    return () => clearInterval(interval);
  }, [showPlaceholder, handleReload]);

  useEffect(() => {
    const reload = async () => {
      setChartLabelString();
      await getLastHourData();
    }
    reload();
  }, [
    minVelocitySetting,
    maxVelocitySetting,
    minWaterRateSetting,
    maxWaterRateSetting,
    minWaterLevelSetting,
    maxWaterLevelSetting,
    dataDialog?.deviceId,
  ])

  useEffect(() => {
    if (!dataDialog?.deviceId) return;
    const start = async () => {
      setChartLabelString();
      await getLastHourData();
    };
    start();
  }, [dataDialog?.deviceId]);

  const dispatch = useDispatch();

  if (showPlaceholder) {
    return (
      <div className="absolute top-0 text-center" style={{ width: '600px', height: '200px' }}>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">デバイスを選択してください</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute top-0 text-center"
      style={{ width: '1000px', height: 'auto' }}
      onMouseMove={(e) => {
        e.stopPropagation();
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
      }}
      onClick={(e) => {
        if (csvLinkRef.current) {
          return;
        }
        e.stopPropagation();
        e.preventDefault();
        e.nativeEvent.stopImmediatePropagation();
      }}>
      <div
        id="DialogWaterLevel"
        className="relative m-auto w-full max-md:h-[100vh] top-0 flex items-center"
      >
        {/* <!-- Modal content --> */}
        <div className="relative rounded-lg bg-white shadow dark:bg-gray-700 w-full">
          {/* <!-- Modal header --> */}
          <div className="flex flex-wrap items-center justify-between rounded-t border-b p-3 dark:border-gray-600">
            <p className="font-bold">{dataDialog.pos}</p>
            <div className="flex items-center gap-2">
              {/* Zoom controls */}
              <div className="flex items-center gap-1 border rounded px-2 py-1">
                <button
                  onClick={handleZoomIn}
                  disabled={timeRangeHours <= 1}
                  className={twMerge(
                    'font-bold text-lg px-2 py-0 rounded transition-colors',
                    timeRangeHours <= 1
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-[#015CAC] hover:bg-blue-50 cursor-pointer'
                  )}
                  title="ズームイン (時間を短縮)">
                  -
                </button>
                <span className="text-sm font-medium min-w-[35px] text-center">
                  {timeRangeHours}h
                </span>
                <button
                  onClick={handleZoomOut}
                  disabled={timeRangeHours >= 3}
                  className={twMerge(
                    'font-bold text-lg px-2 py-0 rounded transition-colors',
                    timeRangeHours >= 3
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-[#015CAC] hover:bg-blue-50 cursor-pointer'
                  )}
                  title="ズームアウト (時間を延長)">
                  +
                </button>
              </div>
              <AiOutlineReload
                className={twMerge(
                  'cursor-pointer text-[#015CAC] hover:scale-110',
                  isReloading ? 'animate-spin' : 'animate-none',
                )}
                onClick={() => {
                  handleReload();
                }}
                strokeWidth={10}
                title="リロード"
                size={24}
              />
            </div>
          </div>
          {/* <!-- Modal body --> */}
          <div className=" p-3 border-b dark:border-gray-600">
            {/*water level*/}
            {/* <div className="flex items-center space-x-4 mt-2">
              <label htmlFor="minFlow" className="font-medium text-sm whitespace-nowrap">水位</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  id="minFlow"
                  placeholder="Min"
                  className="rounded border p-1 w-20 text-sm"
                  value={minWaterLevelSetting ?? ''}
                  onChange={(e) => handleMinMax(e, 'minWaterLevelSetting')}
                />
                <span className="text-gray-500">-</span>
                <input
                  type="number"
                  id="maxFlow"
                  placeholder="Max"
                  className="rounded border p-1 w-20 text-sm"
                  value={maxWaterLevelSetting ?? ''}
                  onChange={(e) => handleMinMax(e, 'maxWaterLevelSetting')}
                />
              </div>
            </div> */}
            {/* velocity */}
            {/* <div className="flex items-center space-x-4 mt-2 mb-2">
              <label htmlFor="minFlow" className="font-medium text-sm whitespace-nowrap">流速</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  id="minFlow"
                  placeholder="Min"
                  className="rounded border p-1 w-20 text-sm"
                  value={minVelocitySetting ?? ''}
                  onChange={(e) => handleMinMax(e, 'minVelocitySetting')}
                />
                <span className="text-gray-500">-</span>
                <input
                  type="number"
                  id="maxFlow"
                  placeholder="Max"
                  className="rounded border p-1 w-20 text-sm"
                  value={maxVelocitySetting ?? ''}
                  onChange={(e) => handleMinMax(e, 'maxVelocitySetting')}
                />
              </div>
            </div> */}
            <div className='flex'>
              <div style={{ height: '250px', width: '700px' }}>
                <Line redraw={true} data={dataWaterLevel} options={configWaterLevel.options} />
              </div>
              <div style={{ height: '250px', width: '700px' }}>
                <Line redraw={true} data={dataVelocity} options={configVelocity.options} />
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}
